import React from 'react';

import {Source} from 'constants/SourceConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import StorableImportSettingsModalPane from './panes/StorableImportSettingsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  STORABLE_BASE_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';
import * as StorableColumnsUtil from './storables/StorableColumnsUtil';

interface Props {
  initialRegistry?: Source | null;
  onRequestClose: () => void;
  actionOrigin: ActionOrigin;
}

export default class BulkCreatePlatesFlow extends React.Component<Props> {
  static open(props: Omit<Props, 'onRequestClose'>): void {
    LegacyModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkCreatePlatesFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  _getPanes(): Array<Pane> {
    return [
      {
        paneKey: PaneKey.SET_STORABLE_IMPORT_SETTINGS,
        configuration: StorableImportSettingsModalPane.configure({
          schemaSelectorClass: PlateSchemaSelector,
          schemaIdAccessorKey: 'plateSchemaId',
          showProjectSelector: true,
        }),
      },
      {
        paneKey: PaneKey.UPLOAD_SPREADSHEET,
        configuration: UploadSpreadsheetModalPane.configure({
          getExampleImageURL() {
            return '/static/img/spreadsheet-import-example-create-plates.png';
          },
        }),
      },
      {
        paneKey: PaneKey.MAP_COLUMN_NAMES,
        configuration: configureMapColumnNamesModalPane({
          getValidColumnTypes() {
            return [
              ...STORABLE_BASE_COLUMN_TYPES,
              COLUMN_TYPE.PLATE_SCHEMA_FIELD,
              COLUMN_TYPE.STUDY_LABELS,
              COLUMN_TYPE.IGNORE,
            ];
          },
          getRequiredColumnsError(columns: Array<SpreadsheetColumn>) {
            const requiredParentStorableError = StorableColumnsUtil.getRequiredParentStorableError(columns);
            if (requiredParentStorableError) {
              return requiredParentStorableError;
            }
            if (columns.some(({type}) => type !== COLUMN_TYPE.IGNORE)) {
              return null;
            }
            return 'At least one column must be specified.';
          },
          onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForCreatePlates,
        }),
      },
      {
        paneKey: PaneKey.SHOW_ERRORS,
        configuration: ShowErrorsModalPane.configure({
          enableReplaceLinkWithText: true,
          enableSkipErrors: true,
        }),
      },
    ];
  }

  override render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="plate"
        importType={IMPORT_TYPE.PLATES}
        initialSettings={{initialRegistry: this.props.initialRegistry}}
        onImportFromSpreadsheet={SpreadsheetBulkImportActions.createPlatesFromSpreadsheet}
        analyticsMetadata={{actionOrigin: this.props.actionOrigin}}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Import plates"
      />
    );
  }
}
